import React, { useEffect, useState, useCallback } from 'react'
import { useRouter } from 'next/router'
import { usePlausible } from 'next-plausible'

import { creatorUrlBuilders } from '@web-apps/utils-shared'

import { cleanSlug } from './utils'
import { useFetchData, useSplashLoader } from './hooks'
import { LINK_SERVICES, FormDataType } from './types'
import { SplashScreen } from './components/SplashScreen'
import { GetStartedPreview } from './components/GetStartedPreview'
import { GetStartedPageForm } from './components/GetStartedForm'

export const GetStartedPage = () => {
  const router = useRouter()
  const plausible = usePlausible()

  const {
    startAnimation,
    stopAnimation,
    percentage,
    currentSlide,
    isActive: isSplashLoadingActive,
  } = useSplashLoader()

  const [fetchData, { isLoading, data, error }] = useFetchData()

  const partner = router.query.partner
  const email = router.query.email
  const predefinedLinktreeSlug = (router.query[LINK_SERVICES.linktree] ||
    router.query[LINK_SERVICES.profile_1]) as string
  const predefinedInstagramSlug = router.query[
    LINK_SERVICES.instagram
  ] as string

  const initialValues = {
    [LINK_SERVICES.instagram]: predefinedInstagramSlug,
  }

  const [slug, setSlug] = useState(
    (predefinedInstagramSlug || predefinedLinktreeSlug) as string
  )
  const [registrationLink, setRegistrationLink] = useState('')

  useEffect(() => {
    setRegistrationLink(
      creatorUrlBuilders.registerPageURLBuilder({
        slug,
        partner: typeof partner === 'string' ? partner : undefined,
        email: typeof email === 'string' ? email : undefined,
      })
    )
  }, [slug, partner, email])

  const sendAnalytics = useCallback(
    (eventName: string, params: { [key in string]?: string } = {}) =>
      plausible('get_started', { props: { event: eventName, ...params } }),
    [plausible]
  )

  const handleSubmit = (formData: FormDataType) => {
    sendAnalytics('manual_input_instagram')
    setSlug(formData[LINK_SERVICES.instagram])

    return fetchData(formData)
  }

  useEffect(() => {
    if (predefinedLinktreeSlug || predefinedInstagramSlug) {
      const predefinedSlug = cleanSlug(
        predefinedInstagramSlug || predefinedLinktreeSlug
      )

      if (predefinedSlug) {
        setSlug(predefinedSlug)
        fetchData({
          ...(predefinedLinktreeSlug && {
            [LINK_SERVICES.linktree]: predefinedLinktreeSlug,
          }),
          ...(predefinedInstagramSlug && {
            [LINK_SERVICES.instagram]: predefinedInstagramSlug,
          }),
        })
      }
    }
  }, [predefinedLinktreeSlug, predefinedInstagramSlug, fetchData])

  useEffect(() => {
    if (isLoading && !isSplashLoadingActive) startAnimation()
    if (!isLoading) stopAnimation()
  }, [isLoading, isSplashLoadingActive, startAnimation, stopAnimation])

  if (isSplashLoadingActive && currentSlide) {
    return <SplashScreen slide={currentSlide} percentage={percentage} />
  }

  if (data) {
    return (
      <GetStartedPreview
        fetchedData={data}
        registrationLink={registrationLink}
        handleAnalytics={sendAnalytics}
      />
    )
  }

  return (
    <GetStartedPageForm
      initialValues={initialValues}
      isLoading={isLoading}
      handleSubmit={handleSubmit}
      registrationLink={registrationLink}
      handleAnalytics={sendAnalytics}
      error={error}
      partner={typeof partner === 'string' ? partner : undefined}
    />
  )
}
